// NoReasonFlow — the per-booking sub-flow that expands after a veteran taps
// "No, it didn't". It collects *why* the service didn't happen (one of five
// reasons) plus the small bit of per-reason detail ops needs to raise the
// right staff task, then hands the finished (reason, detail) pair back to the
// parent BookingCard via onComplete.
//
// The flow is a tiny step machine kept entirely local to this component:
//   'reason'     — the five reason buttons (the entry step)
//   'reschedule' — rescheduled_* → "has a new date been set?" (+ optional date)
//   'wrong_date' — wrong_date    → usual weekday + time
//   'contact'    — wants_contact → call or SMS
//
// Only `no_show` finishes straight from the reason list; the other four route
// into a detail step. The detail payload handed to onComplete contains ONLY
// the fields that reason actually fills (the server/api util treats a present
// `detail` truthily, so an empty {} would be sent — `no_show` therefore passes
// `null`, not `{}`).

import { useState } from 'react';
import type { DidNotAttendDetail, DidNotAttendReason } from '../types';

export interface NoReasonFlowProps {
  bookingId: string;
  disabled: boolean;
  /** Fired when the veteran has a complete, valid reason (+ detail). The
   *  parent uses this to mark the "no" answer complete and stash the payload
   *  for submit. `detail` is `null` for `no_show` (no per-reason fields). */
  onComplete: (reason: DidNotAttendReason, detail: DidNotAttendDetail | null) => void;
  /** Fired when the veteran backs out to re-pick yes/no. */
  onReset: () => void;
}

type Step = 'reason' | 'reschedule' | 'wrong_date' | 'contact';

const WEEKDAYS: Array<{ value: string; label: string }> = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

/** Today as 'YYYY-MM-DD' for the date input's `min` (no Date.prototype.at — ES2020 target). */
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Half-hour service-time slots, 6:00 AM → 8:00 PM inclusive. `value` stays the
 *  24-hour HH:MM the data contract / server regex expects; `label` is the
 *  friendly 12-hour form shown in the dropdown. Generated rather than hand-listed. */
function buildTimeSlots(): Array<{ value: string; label: string }> {
  const slots: Array<{ value: string; label: string }> = [];
  for (let minutes = 6 * 60; minutes <= 20 * 60; minutes += 30) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const label = `${h12}:${String(m).padStart(2, '0')} ${period}`;
    slots.push({ value, label });
  }
  return slots;
}

const TIME_SLOTS = buildTimeSlots();

export function NoReasonFlow({ bookingId, disabled, onComplete, onReset }: NoReasonFlowProps) {
  const [step, setStep] = useState<Step>('reason');
  // Which rescheduled_* reason we routed in with — the reschedule step needs it
  // to report the correct reason code back to onComplete.
  const [rescheduleReason, setRescheduleReason] =
    useState<'rescheduled_provider' | 'rescheduled_self'>('rescheduled_provider');
  // reschedule step: whether the "pick a date" picker is showing + its value.
  const [pickingDate, setPickingDate] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  // wrong_date step fields.
  const [usualDay, setUsualDay] = useState('');
  const [usualTime, setUsualTime] = useState('');

  // Reset all the in-progress detail and bounce back to the reason list. Used
  // by the "← back to reasons" affordance within a detail step.
  const backToReasons = () => {
    setStep('reason');
    setPickingDate(false);
    setRescheduleDate('');
    setUsualDay('');
    setUsualTime('');
  };

  const reasonStep = (
    <div className="reason-list" role="group" aria-label="Why didn't it happen?">
      <p className="reason-lead">No problem — what happened?</p>
      <button
        type="button"
        className="reason-btn"
        disabled={disabled}
        onClick={() => {
          setRescheduleReason('rescheduled_provider');
          setStep('reschedule');
        }}
      >
        Rescheduled by provider
      </button>
      <button
        type="button"
        className="reason-btn"
        disabled={disabled}
        onClick={() => {
          setRescheduleReason('rescheduled_self');
          setStep('reschedule');
        }}
      >
        Rescheduled by you
      </button>
      <button
        type="button"
        className="reason-btn"
        disabled={disabled}
        onClick={() => setStep('wrong_date')}
      >
        Wrong service date
      </button>
      <button
        type="button"
        className="reason-btn"
        disabled={disabled}
        onClick={() => onComplete('no_show', null)}
      >
        Did not show at all
      </button>
      <button
        type="button"
        className="reason-btn"
        disabled={disabled}
        onClick={() => setStep('contact')}
      >
        I'd like a staff member to call or SMS me
      </button>
    </div>
  );

  const changeLink = (
    <button type="button" className="reason-change" onClick={onReset} disabled={disabled}>
      ← change
    </button>
  );

  // "back to reasons" lets a mis-tap inside a detail step be corrected without
  // collapsing the whole flow; "change" (onReset) backs all the way out to yes/no.
  const backRow = (
    <div className="reason-back-row">
      <button
        type="button"
        className="reason-back"
        onClick={backToReasons}
        disabled={disabled}
      >
        ← back to reasons
      </button>
      {changeLink}
    </div>
  );

  if (step === 'reschedule') {
    const dateFieldId = `reschedule-date-${bookingId}`;
    return (
      <div className="reason-detail">
        <p className="reason-lead">Has a new date been set?</p>
        {!pickingDate ? (
          <div className="reason-choices">
            <button
              type="button"
              className="reason-btn"
              disabled={disabled}
              onClick={() => onComplete(rescheduleReason, { rescheduleDate: null })}
            >
              Not yet
            </button>
            <button
              type="button"
              className="reason-btn"
              disabled={disabled}
              onClick={() => setPickingDate(true)}
            >
              Pick a date
            </button>
          </div>
        ) : (
          <div className="reason-field">
            <label htmlFor={dateFieldId}>New date</label>
            <input
              id={dateFieldId}
              type="date"
              min={todayISO()}
              value={rescheduleDate}
              disabled={disabled}
              onChange={(e) => setRescheduleDate(e.target.value)}
            />
            <button
              type="button"
              className="reason-confirm"
              disabled={disabled || !rescheduleDate}
              onClick={() => onComplete(rescheduleReason, { rescheduleDate })}
            >
              Confirm
            </button>
          </div>
        )}
        {backRow}
      </div>
    );
  }

  if (step === 'wrong_date') {
    const dayFieldId = `usual-day-${bookingId}`;
    const timeFieldId = `usual-time-${bookingId}`;
    const ready = !!usualDay && !!usualTime;
    return (
      <div className="reason-detail">
        <p className="reason-lead">What's your usual service day &amp; time?</p>
        <div className="reason-field">
          <label htmlFor={dayFieldId}>Usual day</label>
          <select
            id={dayFieldId}
            value={usualDay}
            disabled={disabled}
            onChange={(e) => setUsualDay(e.target.value)}
          >
            <option value="">Select a day…</option>
            {WEEKDAYS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <div className="reason-field">
          <label htmlFor={timeFieldId}>Usual time</label>
          <select
            id={timeFieldId}
            value={usualTime}
            disabled={disabled}
            onChange={(e) => setUsualTime(e.target.value)}
          >
            <option value="" disabled>
              Select a time…
            </option>
            {TIME_SLOTS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="reason-confirm"
          disabled={disabled || !ready}
          onClick={() => onComplete('wrong_date', { usualDay, usualTime })}
        >
          Confirm
        </button>
        {backRow}
      </div>
    );
  }

  if (step === 'contact') {
    return (
      <div className="reason-detail">
        <p className="reason-lead">How would you like us to reach you?</p>
        <div className="reason-choices">
          <button
            type="button"
            className="reason-btn"
            disabled={disabled}
            onClick={() => onComplete('wants_contact', { contactPreference: 'call' })}
          >
            Call
          </button>
          <button
            type="button"
            className="reason-btn"
            disabled={disabled}
            onClick={() => onComplete('wants_contact', { contactPreference: 'sms' })}
          >
            SMS
          </button>
        </div>
        {backRow}
      </div>
    );
  }

  return reasonStep;
}
